import * as AgenticV2API from "@/services/api/agentic-v2";
import type { FaClientDataGathering } from "../types";

const TERMINAL_TASK_STATUSES = new Set([
  "done",
  "auto_closed",
  "cancelled",
  "failed",
  "blocked",
  "needs_review",
]);

/** Stable fingerprint for detecting checklist / monitoring rollup changes. */
export function dgChecklistFingerprint(summary: FaClientDataGathering): string {
  return JSON.stringify({
    synced: summary.monitoring_last_synced_at ?? null,
    pct: summary.checklist_complete_pct,
    missing: summary.checklist_missing_count,
    artifact: summary.checklist_artifact_id ?? null,
    summary: summary.checklist?.data?.overall_summary ?? null,
  });
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const timer = window.setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        window.clearTimeout(timer);
        reject(new DOMException("Aborted", "AbortError"));
      },
      { once: true },
    );
  });
}

/** Poll until DG checklist data changes or the monitoring task reaches a terminal status. */
export async function waitForUpdatedDgChecklist(
  faClientId: string,
  baselineFingerprint: string,
  monitoringTaskId: string | null | undefined,
  options: { maxMs?: number; intervalMs?: number; signal?: AbortSignal } = {},
): Promise<FaClientDataGathering | null> {
  const maxMs = options.maxMs ?? 120_000;
  const intervalMs = options.intervalMs ?? 2000;
  const deadline = Date.now() + maxMs;

  while (Date.now() < deadline) {
    await sleep(intervalMs, options.signal);

    let taskTerminal = false;
    if (monitoringTaskId) {
      try {
        const task = await AgenticV2API.getTask(monitoringTaskId);
        taskTerminal = TERMINAL_TASK_STATUSES.has(task.status);
      } catch {
        // Keep polling — task may not be readable yet.
      }
    }

    try {
      const next = await AgenticV2API.getFaClientDataGathering(faClientId);
      const changed = dgChecklistFingerprint(next) !== baselineFingerprint;
      if (changed || taskTerminal) {
        return next;
      }
    } catch {
      // Transient fetch errors — try again until timeout.
    }
  }

  return null;
}
